import { useQuery } from "@tanstack/react-query"
import { Breadcrumb, Button, Card, Col, Divider, Form, Input, Modal, Row, Select, Typography, message } from "antd"
import type React from "react"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import SystemVocabularyTable from "../system-vocabulary/components/SystemVocabularyTable"
import { setService } from "@/services/dictionary/setApi"
import { vocabularyService } from "@/services/dictionary/vocabularyApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { AppstoreOutlined, ArrowLeftOutlined, PlusOutlined, FolderOutlined, FileTextOutlined } from "@ant-design/icons"

const { Text } = Typography

export const SystemSetEditPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [searchParams, setSearchParams] = useTableParams({
    ...PAGE_CONFIG,
    search: "",
  })
  const [total, setTotal] = useState<number>(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  // Query Set info
  const { data: setInfoResult } = useQuery({
    queryKey: ["set_info", id],
    queryFn: () => setService.get(Number(id)),
    enabled: !!id,
  })

  // Query Breadcrumbs
  const { data: breadcrumbsResult } = useQuery({
    queryKey: ["set_breadcrumbs_edit", id],
    queryFn: () => setService.getBreadcrumbs(Number(id)),
    enabled: !!id,
  })

  // Query Vocabularies for this set
  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["vocabulary_list", searchParams, id],
    queryFn: () => {
      return vocabularyService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        type: "system",
        set_id: Number(id),
      } as any)
    },
    enabled: !!id,
  })

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const setInfo = setInfoResult?.data
  const breadcrumbs = breadcrumbsResult?.data || []

  // Add Vocab State
  const [isAddVocabModalOpen, setIsAddVocabModalOpen] = useState(false)
  const [vocabText, setVocabText] = useState("")
  const [isAddingVocab, setIsAddingVocab] = useState(false)

  // Edit Vocab State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingVocab, setEditingVocab] = useState<any>(null)
  const [editForm] = Form.useForm()
  const [isUpdatingVocab, setIsUpdatingVocab] = useState(false)

  const handleEditVocab = (record: any) => {
    setEditingVocab(record)
    editForm.setFieldsValue({
      text: record.text,
      part_of_speech: record.part_of_speech,
      ipa: record.ipa,
      meaning: record.meaning,
      definition: record.definition,
      example: record.example,
      example_meaning: record.example_meaning,
      synonyms: Array.isArray(record.synonyms) ? record.synonyms.join(", ") : (record.synonyms || ""),
      antonyms: Array.isArray(record.antonyms) ? record.antonyms.join(", ") : (record.antonyms || ""),
    })
    setIsEditModalOpen(true)
  }

  const handleEditVocabSubmit = async () => {
    try {
      const values = await editForm.validateFields()
      setIsUpdatingVocab(true)
      const payload = {
        ...values,
        synonyms: typeof values.synonyms === "string"
          ? values.synonyms.split(",").map((s: string) => s.trim()).filter(Boolean)
          : (values.synonyms || []),
        antonyms: typeof values.antonyms === "string"
          ? values.antonyms.split(",").map((s: string) => s.trim()).filter(Boolean)
          : (values.antonyms || []),
      }
      const res = await vocabularyService.update(editingVocab.id, payload)
      if (res.code === 200) {
        message.success("Cập nhật từ vựng thành công")
        setIsEditModalOpen(false)
        setEditingVocab(null)
        editForm.resetFields()
        refetch()
      } else {
        message.error(res.message || "Cập nhật thất bại")
      }
    } catch (err: any) {
      message.error(err.message || "Có lỗi xảy ra")
    } finally {
      setIsUpdatingVocab(false)
    }
  }

  const parseVocabularies = (text: string) => {
    const lines = text.trim().split("\n")
    if (lines.length === 0 || text.trim() === "") return []

    const results = []
    for (let i = 0; i < lines.length; i++) {
      const cols = lines[i].split("\t").map((c) => c.trim())
      if (!cols[0]) continue

      // Format cố định: Từ vựng, Từ loại, Nghĩa tiếng Việt, Ví dụ, Dịch ví dụ
      const item: any = {
        text: cols[0] || "",
        part_of_speech: cols[1] || "",
        meaning: cols[2] || "",
        example: cols[3] || "",
        example_meaning: cols[4] || "",
      }
      results.push(item)
    }
    return results
  }

  const handleAddVocabSubmit = async () => {
    try {
      const vocabs = parseVocabularies(vocabText)
      if (vocabs.length === 0) {
        message.warning("Không tìm thấy dữ liệu hợp lệ. Vui lòng kiểm tra lại cấu trúc.")
        return
      }

      setIsAddingVocab(true)
      const res = await vocabularyService.bulkAdd({
        set_id: Number(id),
        vocabularies: vocabs,
      })

      if (res.code === 200) {
        message.success(`Thêm thành công ${vocabs.length} từ vựng!`)
        setIsAddVocabModalOpen(false)
        setVocabText("")
        refetch()
      } else {
        message.error(res.message || "Thêm thất bại")
      }
    } catch (err: any) {
      message.error(err.message || "Có lỗi xảy ra")
    } finally {
      setIsAddingVocab(false)
    }
  }

  const breadcrumbItems = [
    {
      title: (
        <div
          className="flex cursor-pointer items-center gap-1 font-medium text-gray-500 transition-colors hover:text-blue-500"
          onClick={() => navigate("/notebook/system-set")}
        >
          <FolderOutlined /> Gốc (Root)
        </div>
      ),
    },
  ]

  if (breadcrumbs.length > 0) {
    breadcrumbs.forEach((b, idx) => {
      const isLast = idx === breadcrumbs.length - 1
      breadcrumbItems.push({
        title: (
          <div
            className={`flex items-center gap-1 font-medium transition-colors ${isLast ? "text-black font-semibold" : "cursor-pointer text-gray-500 hover:text-blue-500"}`}
            onClick={() => {
              if (!isLast) {
                navigate(`/notebook/system-set?parent_id=${b.id}`)
              }
            }}
          >
            {b.node_type === "folder" ? (
              <FolderOutlined />
            ) : (
              <FileTextOutlined className={isLast ? "text-green-600" : ""} />
            )}{" "}
            {b.title}
          </div>
        ),
      })
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} type="default" size="small" />
        <div className="flex flex-col gap-1 overflow-hidden">
          <Breadcrumb items={breadcrumbItems} />
          {setInfo?.description && (
            <Text type="secondary" className="text-xs">
              {setInfo.description}
            </Text>
          )}
        </div>
      </div>

      <Card
        className="flex min-h-0 grow flex-col shadow-sm"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <h2>Danh sách từ vựng</h2>
              <Divider orientation="vertical" />
              <Text type="secondary" className="text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddVocabModalOpen(true)}>
                Thêm từ vựng (Paste)
              </Button>
            </div>
          </div>
        }
      >
        <SystemVocabularyTable
          data={result?.data || []}
          loading={isFetching}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          onEdit={handleEditVocab}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: result?.paginate?.total || total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <Modal
        title="Thêm từ vựng hàng loạt (Copy & Paste từ Excel)"
        open={isAddVocabModalOpen}
        onOk={handleAddVocabSubmit}
        confirmLoading={isAddingVocab}
        onCancel={() => {
          setIsAddVocabModalOpen(false)
          setVocabText("")
        }}
        okText="Thêm từ vựng"
        cancelText="Hủy"
        width={700}
      >
        <div className="mt-4 flex flex-col gap-2">
          <Text type="secondary">
            Hãy copy bảng dữ liệu từ Excel / Google Sheets và paste vào khung bên dưới. Dữ liệu{" "}
            <b className="text-red-500">không bao gồm dòng tiêu đề</b>.
            <br />
            Thứ tự các cột bắt buộc: <b>Từ vựng, Từ loại, Nghĩa tiếng Việt, Ví dụ, Dịch ví dụ</b>.
          </Text>
          <Input.TextArea
            rows={10}
            placeholder={`special\tAdjective\tđặc biệt\tThis is a special day.\tĐây là một ngày đặc biệt.`}
            value={vocabText}
            onChange={(e) => setVocabText(e.target.value)}
            style={{ whiteSpace: "pre" }}
          />
        </div>
      </Modal>

      {/* Modal Edit Vocabulary */}
      <Modal
        title="Chỉnh sửa từ vựng"
        open={isEditModalOpen}
        onOk={handleEditVocabSubmit}
        confirmLoading={isUpdatingVocab}
        onCancel={() => {
          setIsEditModalOpen(false)
          setEditingVocab(null)
          editForm.resetFields()
        }}
        okText="Lưu thay đổi"
        cancelText="Hủy"
        width={650}
      >
        <Form form={editForm} layout="vertical" className="mt-4">
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item
                name="text"
                label="Từ vựng / Cụm từ"
                rules={[{ required: true, message: "Vui lòng nhập từ vựng" }]}
              >
                <Input placeholder="Ví dụ: hello" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="part_of_speech" label="Từ loại">
                <Select
                  placeholder="Chọn từ loại"
                  allowClear
                  options={[
                    { value: "noun", label: "Noun (Danh từ)" },
                    { value: "verb", label: "Verb (Động từ)" },
                    { value: "adjective", label: "Adjective (Tính từ)" },
                    { value: "adverb", label: "Adverb (Trạng từ)" },
                    { value: "phrase", label: "Phrase (Cụm từ)" },
                    { value: "idiom", label: "Idiom (Thành ngữ)" },
                    { value: "collocation", label: "Collocation (Cụm kết hợp)" },
                    { value: "phrasal verb", label: "Phrasal Verb (Cụm động từ)" },
                    { value: "preposition", label: "Preposition (Giới từ)" },
                    { value: "conjunction", label: "Conjunction (Liên từ)" },
                    { value: "interjection", label: "Interjection (Thán từ)" },
                    { value: "pronoun", label: "Pronoun (Đại từ)" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="ipa" label="Phiên âm (IPA)">
                <Input placeholder="Ví dụ: /həˈləʊ/" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="meaning"
            label="Nghĩa tiếng Việt"
            rules={[{ required: true, message: "Vui lòng nhập nghĩa tiếng Việt" }]}
          >
            <Input.TextArea rows={2} placeholder="Nghĩa tiếng Việt của từ..." />
          </Form.Item>

          <Form.Item name="definition" label="Định nghĩa tiếng Anh (tùy chọn)">
            <Input.TextArea rows={2} placeholder="English definition..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="example" label="Ví dụ tiếng Anh">
                <Input.TextArea rows={2} placeholder="Example sentence..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="example_meaning" label="Dịch nghĩa ví dụ">
                <Input.TextArea rows={2} placeholder="Dịch nghĩa câu ví dụ..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="synonyms"
                label="Từ đồng nghĩa (Synonyms)"
                extra="Nhập các từ cách nhau bởi dấu phẩy (,)"
              >
                <Input placeholder="Ví dụ: glad, joyful, cheerful" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="antonyms"
                label="Từ trái nghĩa (Antonyms)"
                extra="Nhập các từ cách nhau bởi dấu phẩy (,)"
              >
                <Input placeholder="Ví dụ: sad, sorrowful, unhappy" allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default SystemSetEditPage
